/* Common Imports */

import React from "react";
import { styled } from "@mui/system";

/* MUI Imports */

import { Typography } from "@mui/material";
import { Edit } from "@mui/icons-material";

/* Helper Imports */

import { brokerageBasisLabel, brokeragePayout } from "../brokerage-config";

/* Styled Components */

const Container = styled("div")(() => ({
	display: "flex",
	flexDirection: "column",
	gap: "1.5rem",
}));

const ReviewCard = styled("div")(() => ({
	border: "1px solid #e0e0e0",
	borderRadius: "0.75rem",
	padding: "1.25rem",
}));

const CardHeader = styled("div")(() => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	marginBottom: "1rem",
}));

const CardTitle = styled(Typography)(() => ({
	fontSize: "1rem",
	fontWeight: 600,
	color: "#212121",
}));

const EditButton = styled("div")(() => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	gap: "0.25rem",
	cursor: "pointer",
	color: "#E07934",
	fontSize: "0.875rem",
	fontWeight: 500,
}));

const FieldGrid = styled("div")(() => ({
	display: "grid",
	gridTemplateColumns: "1fr 1fr",
	gap: "1rem",
}));

const FieldItem = styled("div")(() => ({
	display: "flex",
	flexDirection: "column",
	gap: "0.125rem",
}));

const FieldLabel = styled(Typography)(() => ({
	fontSize: "0.75rem",
	fontWeight: 400,
	color: "#9e9e9e",
}));

const FieldValue = styled(Typography)(() => ({
	fontSize: "0.875rem",
	fontWeight: 500,
	color: "#212121",
}));

/* Helper to format budget */

const formatBudget = (value: number): string => {
	if (value >= 10000000) return "₹" + (value / 10000000).toFixed(1) + " Cr";
	if (value >= 100000) return "₹" + (value / 100000).toFixed(1) + "L";
	if (value >= 1000) return "₹" + (value / 1000).toFixed(0) + "K";
	return "₹" + value;
};

/* Helper to format BHK */

const formatBhk = (bhk: number | number[]): string => {
	if (Array.isArray(bhk)) {
		return bhk.map((b) => (b === 0.5 ? "1 RK" : b >= 3 ? "3+ BHK" : b + " BHK")).join(", ");
	}
	if (bhk === 0.5) return "1 RK";
	if (bhk >= 3) return "3+ BHK";
	return bhk + " BHK";
};

/* Helper to format possession */

const formatPossession = (value: string): string => {
	if (!value) return "-";
	return value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

/* Component */

const ReviewStep = ({
	formData,
	onEditStep,
}: {
	formData: any;
	onEditStep: (step: number) => void;
}) => {
	const locationNames = formData.locations
		.map((loc: any) => loc.location_title || loc.locality)
		.filter((name: string) => name)
		.join(", ");

	return (
		<Container>
			{/* Requirement Details Card */}

			<ReviewCard>
				<CardHeader>
					<CardTitle>Requirement Details</CardTitle>
					<EditButton onClick={() => onEditStep(0)}>
						<Edit sx={{ fontSize: "0.875rem" }} />
						Edit
					</EditButton>
				</CardHeader>
				<FieldGrid>
					<FieldItem>
						<FieldLabel>Requirement Type</FieldLabel>
						<FieldValue>
							{formData.transaction_type
								? formData.transaction_type.charAt(0).toUpperCase() + formData.transaction_type.slice(1)
								: "-"}
						</FieldValue>
					</FieldItem>
					<FieldItem>
						<FieldLabel>Property Category</FieldLabel>
						<FieldValue>
							{formData.property_category
								? formData.property_category.charAt(0).toUpperCase() + formData.property_category.slice(1)
								: "-"}
						</FieldValue>
					</FieldItem>
					{formData.property_type.length > 0 && (
						<FieldItem>
							<FieldLabel>Property type</FieldLabel>
							<FieldValue>{formData.property_type.join(", ")}</FieldValue>
						</FieldItem>
					)}
					{formData.configuration.bhk?.length > 0 && (
						<FieldItem>
							<FieldLabel>Configuration</FieldLabel>
							<FieldValue>{formatBhk(formData.configuration.bhk)}</FieldValue>
						</FieldItem>
					)}
					{locationNames && (
						<FieldItem>
							<FieldLabel>Location</FieldLabel>
							<FieldValue>{locationNames}</FieldValue>
						</FieldItem>
					)}
					<FieldItem>
						<FieldLabel>Budget</FieldLabel>
						<FieldValue>
							{formatBudget(formData.budget.max || formData.budget.min)}
							{formData.budget.is_flexible ? " (Flexible)" : ""}
						</FieldValue>
					</FieldItem>
				</FieldGrid>
			</ReviewCard>

			{/* Additional Details Card */}

			<ReviewCard>
				<CardHeader>
					<CardTitle>Additional Details</CardTitle>
					<EditButton onClick={() => onEditStep(1)}>
						<Edit sx={{ fontSize: "0.875rem" }} />
						Edit
					</EditButton>
				</CardHeader>
				<FieldGrid>
					{formData.configuration.possession_by && (
						<FieldItem>
							<FieldLabel>Possession</FieldLabel>
							<FieldValue>{formatPossession(formData.configuration.possession_by)}</FieldValue>
						</FieldItem>
					)}
					{formData.configuration.furnishing && (
						<FieldItem>
							<FieldLabel>Furnishing</FieldLabel>
							<FieldValue>{formatPossession(formData.configuration.furnishing)}</FieldValue>
						</FieldItem>
					)}
					{formData.brokerage.is_offering_brokerage && (
						<FieldItem>
							<FieldLabel>Brokerage</FieldLabel>
							<FieldValue>
								{brokerageBasisLabel(formData.brokerage.value, formData.transaction_type)}
								{formData.budget.max
									? ` (Approx. ₹${brokeragePayout(formData.budget.max, formData.brokerage.value, formData.transaction_type).toLocaleString("en-IN")})`
									: ""}
							</FieldValue>
						</FieldItem>
					)}
					{formData.notes && (
						<FieldItem style={{ gridColumn: "1 / -1" }}>
							<FieldLabel>Notes</FieldLabel>
							<FieldValue>{formData.notes}</FieldValue>
						</FieldItem>
					)}
				</FieldGrid>
			</ReviewCard>
		</Container>
	);
};

export default ReviewStep;
